import ExcelJs from 'exceljs';
import i18n from 'i18next';
import { SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS } from '../contants/ShipmentEnums';
import { getDateLabel } from './dates';
import { generateExcelColumnNames, writeFileAndDownload } from './excelUtils';
import { hyphenOneToEmptyInObject } from './utilz';

export const exportRateShopDetails = (rateShops) => {
  const sheetName = `RateShop ${i18n.t('translation:visibility.detailCard.detailsTab.title')}`;
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const rows = [];
  rateShops.forEach((row) => {
    // biome-ignore lint/style/noParameterAssign: disabled
    row = hyphenOneToEmptyInObject(row);
    const arr = [];
    arr.push(row.rateShopKey);
    arr.push(row.carrierName);
    arr.push(row.convertedCost);
    arr.push(row.convertedCurrencyCode);
    arr.push(row.pricingBasis);
    arr.push(row.standardDaysTransit);
    arr.push(row.cargoType);
    arr.push(row.serviceDesc);
    arr.push(row.trailerkey);
    arr.push(row.equipmentTypeDetail);
    arr.push(row.userCreatedBy);
    arr.push(getDateLabel(row.createdOn));
    arr.push(row.effectiveTo);
    arr.push(row.bidComment);
    arr.push(row.spotQuoteNumber);
    rows.push(arr);
  });
  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS,
    rows: rows,
    autoFilterEnabled: true,
  });
  sheet.autoFilter = 'A1:O1';
  const columnsList = generateExcelColumnNames(SHIPMENT_RATE_SHOP_RESULTS_EXPORT_COLUMNS.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '7A7A7A' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values[1];
    switch (expr) {
      case 'RateShopKey':
        return { width: 20 };
      case 'Transportation Provider':
        return { width: 40 };
      case 'Converted Cost':
        return { width: 15 };
      case 'Converted Currency Code':
        return { width: 15 };
      case 'Pricing Basis':
        return { width: 30 };
      case 'Standard Transit Days':
        return { width: 30 };
      case 'Cargo Type':
        return { width: 25 };
      case 'Service Description':
        return { width: 30 };
      case 'Equipment Type':
        return { width: 20 };
      case 'Equipment Type Detail':
        return { width: 30 };
      case 'User Submitted':
        return { width: 20 };
      case i18n.t('translation:default.fields.createdOn.title'):
        return { width: 20 };
      case 'Bid Expiry Date':
        return { width: 20 };
      case 'Bid Comment':
        return { width: 30 };
      case 'Spot Quote Number':
        return { width: 30 };
      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(sheetName, buffer);
  });
};

export const exportShipmentListToExcel = (columns, rows, sheetNameValue, fileName) => {
  const sheetName = sheetNameValue || 'Shipment Search';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const { expShipDate, expDeliveryDate, shipmentDate } = columns.reduce(
    (acc, obj, index) => {
      if (obj.name === 'Expected Ship Date') {
        acc.expShipDate = index;
      } else if (obj.name === 'Expected Delivery Date') {
        acc.expDeliveryDate = index;
      } else if (obj.name === 'Shipment Date') {
        acc.shipmentDate = index;
      }
      return acc;
    },
    { expShipDate: -1, expDeliveryDate: -1, shipmentDate: -1 },
  );

  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: columns,
    rows: rows,
    autoFilterEnabled: true,
  });
  sheet.views = [
    {
      state: 'frozen',
      xSplit: 1,
      activeCell: 'A1',
    },
  ];

  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '75A72E' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  sheet.columns = sheet.columns.map((_e) => ({ width: 30 }));
  let rowIndex = 0;
  rows.forEach(() => {
    columnsList.forEach((column, columnIndex) => {
      const cell = sheet.getCell(`${column}${rowIndex + 2}`);
      if (columnIndex === expShipDate || columnIndex === expDeliveryDate || columnIndex === shipmentDate) {
        cell.numFmt = 'mmm dd, yyyy';
      }
    });
    rowIndex++;
  });

  if (fileName) {
    workbook.xlsx.writeBuffer().then((buffer) => {
      writeFileAndDownload(`${fileName} ${new Date().toLocaleDateString()}`, buffer);
    });
  } else {
    workbook.xlsx.writeBuffer().then((buffer) => {
      writeFileAndDownload(`Shipment Search ${new Date().toLocaleDateString()}`, buffer);
    });
  }
};

export const exportPurchaseOrder = (columns, rows) => {
  const sheetName = 'Purchase Orders';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  const { orderShipDateIndex, deliveryDateIndex, createdOnIndex } = columns.reduce(
    (acc, obj, index) => {
      if (obj.key === 'OrderShipDate') {
        acc.orderShipDateIndex = index;
      } else if (obj.key === 'DeliveryDate') {
        acc.deliveryDateIndex = index;
      } else if (obj.key === 'CreatedOn') {
        acc.createdOnIndex = index;
      }
      return acc;
    },
    { orderShipDateIndex: -1, deliveryDateIndex: -1, createdOnIndex: -1 },
  );

  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: columns,
    rows: rows,
    autoFilterEnabled: true,
  });

  sheet.views = [
    {
      state: 'frozen',
      xSplit: 1,
      activeCell: 'A1',
    },
  ];
  const columnsList = generateExcelColumnNames(columns.length);

  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '75A72E' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  sheet.columns = sheet.columns.map((_e) => ({ width: 30 }));
  let rowIndex = 0;
  rows.forEach(() => {
    columnsList.forEach((column, columnIndex) => {
      const cell = sheet.getCell(`${column}${rowIndex + 2}`);
      if (columnIndex === orderShipDateIndex || columnIndex === deliveryDateIndex || columnIndex === createdOnIndex) {
        cell.numFmt = 'mmm dd, yyyy';
      }
    });
    rowIndex++;
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`Purchase_Order_List ${new Date().toLocaleDateString()}`, buffer);
  });
};

export const exportCreateShipmentDetails = (shipmentItems, formData) => {
  const sheetName = `Create Shipment Item ${i18n.t('translation:visibility.detailCard.detailsTab.title')}`;
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});

  const rows = [];
  shipmentItems.forEach((row, index) => {
    const arr = [];
    const quantityShipped = `PoQuantityShipped${index}`;
    const countryOfOrigin = `countryOfOrigin${index}`;
    const eccn = `eccn${index}`;
    const htcCode = `htcCode${index}`;
    arr.push(row.poNumber);
    arr.push(row?.partNumber || row?.nmfcItemNumber || row?.nmfcNumber);
    arr.push(row.lineNum);
    arr.push(row.poQuantity);
    arr.push(row.poQuantityLeft);
    arr.push(row.unitPrice);
    arr.push(formData[quantityShipped]);
    arr.push(row.expShipDate);
    arr.push(row.crateNo);
    arr.push(row.productDesc);
    arr.push(formData[htcCode]);
    arr.push(formData[eccn]);
    arr.push(formData[countryOfOrigin]);
    rows.push(arr);
  });

  const columns = [
    {
      name: 'Order Number',
      label: i18n.t('translation:default.fields.orderNumber'),
    },
    {
      name: 'Part Number',
      label: i18n.t('translation:default.fields.partNumber'),
    },
    {
      name: 'line Number',
      label: i18n.t('translation:default.fields.lineNumber.label'),
    },
    {
      name: 'Quantity',
      label: i18n.t('translation:default.fields.quantity.label'),
    },
    {
      name: 'Quantity Left',
      label: i18n.t('translation:default.fields.quantityLeft'),
    },
    { name: 'Price', label: i18n.t('translation:default.fields.price') },
    {
      name: 'Quantity Shipped',
      label: i18n.t('translation:default.fields.quantityShipped'),
    },
    {
      name: 'New Ship Date',
      label: i18n.t('translation:default.fields.newShipDate'),
    },
    {
      name: 'Package Id',
      label: i18n.t('translation:default.fields.packageID.label'),
    },
    {
      name: 'Description',
      label: i18n.t('translation:default.fields.description'),
    },
    { name: 'HTS', label: i18n.t('translation:default.fields.hts') },
    { name: 'ECCN', label: i18n.t('translation:default.fields.eccn') },
    {
      name: 'Country Of Origin',
      label: i18n.t('translation:default.fields.countryOfOrigin'),
    },
  ];

  const formattedColumns = columns.map(({ label }) => ({
    name: label || '---',
  }));

  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: formattedColumns,
    rows: rows,
    autoFilterEnabled: true,
  });
  sheet.autoFilter = 'A1:O1';
  const columnsList = generateExcelColumnNames(columns.length);

  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '7A7A7A' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
  });

  sheet.columns = sheet.columns.map((_e) => {
    // const expr = e.values[1];
    return { width: 20 };
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(sheetName, buffer);
  });
};

export const exportShipmentNotificationDistributorsList = (
  columns,
  rows,
  sheetName = 'Shipment Notification Distributors',
) => {
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: columns,
    rows: rows,
    autoFilterEnabled: true,
  });
  sheet.views = [
    {
      state: 'frozen',
      xSplit: 1,
      activeCell: 'A1',
    },
  ];
  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '7A7A7A' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values[1];
    switch (expr) {
      case 'DistributionId':
        return { width: 20 };
      case 'Distribution Name':
        return { width: 35 };
      case 'Description':
        return { width: 50 };
      case 'Status':
        return { width: 20 };

      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload('DistributionList', buffer);
  });
};

export const exportShipmentNotificationRoutingList = (columns, rows, sheetName = 'Shipment Notification Routings') => {
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName, {});
  sheet.addTable({
    name: 'Header',
    ref: 'A1',
    headerRow: true,
    totalsRow: false,
    style: {
      theme: '',
      showRowStripes: false,
    },
    columns: columns,
    rows: rows,
    autoFilterEnabled: true,
  });
  sheet.views = [
    {
      state: 'frozen',
      xSplit: 1,
      activeCell: 'A1',
    },
  ];

  const columnsList = generateExcelColumnNames(columns.length);
  columnsList.forEach((column) => {
    const cell = sheet.getCell(`${column}1`);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '7A7A7A' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: 700,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.columns = sheet.columns.map((e) => {
    const expr = e.values[1];
    switch (expr) {
      case 'RoutingDetailId':
        return { width: 20 };
      case 'Distribution Name':
      case 'Customer Name':
      case 'User Name':
      case 'Transportation Provider':
        return { width: 35 };
      case 'Email List':
        return { width: 30 };
      case 'Status':
        return { width: 20 };

      default:
        return { width: 20 };
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload('NotificationList', buffer);
  });
};

export const exportOverChargeClaims = (columnList, data) =>
  new Promise((resolve, reject) => {
    try {
      {
        const sheetName = 'OverChargeClaim_Search_Result';
        const workbook = new ExcelJs.Workbook();
        const sheet = workbook.addWorksheet(sheetName, {});

        const columns = columnList.map(({ columnName }) => ({
          name: columnName,
        }));

        const rows = [];
        data.forEach((row) => {
          // biome-ignore lint/style/noParameterAssign: disabled
          row = hyphenOneToEmptyInObject(row);
          const arr = [];
          columnList.forEach(({ value }) => {
            arr.push(row[value]);
          });

          rows.push(arr);
        });
        sheet.addTable({
          name: 'Header',
          ref: 'A1',
          headerRow: true,
          totalsRow: false,
          style: {
            theme: '',
            showRowStripes: false,
          },
          columns: columns,
          rows: rows,
        });

        sheet.columns = sheet.columns.map((e) => {
          const expr = e.values[1];
          switch (expr) {
            case 'TransportationProvider':
              return { width: 40 };
            default:
              return { width: 20 };
          }
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
          writeFileAndDownload(sheetName, buffer);
          resolve(true);
        });
      }
    } catch (error) {
      reject(error);
    }
  });

export const exportLossAndDamageClaims = (columnList, data) =>
  new Promise((resolve, reject) => {
    try {
      const sheetName = 'LossAndDamageClaim_Search_Result';
      const workbook = new ExcelJs.Workbook();
      const sheet = workbook.addWorksheet(sheetName, {});

      const columns = columnList;
      sheet.addTable({
        name: 'Header',
        ref: 'A1',
        headerRow: true,
        totalsRow: false,
        style: {
          theme: '',
          showRowStripes: false,
        },
        columns: columns,
        rows: data,
      });

      sheet.columns = sheet.columns.map((e) => {
        const expr = e.values[1];
        switch (expr) {
          case 'Transportation Provider':
            return { width: 40 };
          default:
            return { width: 20 };
        }
      });

      workbook.xlsx.writeBuffer().then((buffer) => {
        writeFileAndDownload(sheetName, buffer);
        resolve(true);
      });
    } catch (error) {
      reject(error);
    }
  });

export const exportQuoteHistoryDetails = (rateShops) => {
  const sheetName = 'QuoteHistory Details';
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  // Define columns
  const columns = [
    { header: 'Quote #', key: 'quoteNumber', width: 20 },
    { header: 'Modified By', key: 'modifiedBy', width: 50 },
    { header: 'Modified On', key: 'modifiedOn', width: 50 },
    { header: 'Tracking Date Time', key: 'trackingDatetime', width: 50 },
    { header: 'Status', key: 'status', width: 50 },
    { header: 'Awarded Notes', key: 'awardedNotes', width: 50 },
    { header: 'Cancellation Reason', key: 'cancellationReason', width: 50 },
  ];
  sheet.columns = columns;

  // Process and add rows
  rateShops.forEach((row) => {
    const processedRow = hyphenOneToEmptyInObject(row);
    sheet.addRow({
      quoteNumber: processedRow.quoteNumber,
      modifiedBy: processedRow.modifiedBy,
      modifiedOn: processedRow.modifiedOn,
      trackingDatetime: processedRow.trackingDatetime,
      status: processedRow.status,
      awardedNotes: processedRow.awardedNotes,
      cancellationReason: processedRow.cancellationReason,
    });
  });

  // Style header row
  sheet.getRow(1).eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '7A7A7A' },
    };
    cell.font = {
      color: { argb: 'FFFFFF' },
      size: 10,
      bold: true,
    };
  });

  // Enable auto filter
  sheet.autoFilter = {
    from: 'A1',
    to: 'E1',
  };

  // Generate and download the Excel file
  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`${sheetName} ${new Date().toLocaleDateString()}`, buffer);
  });
};

export const exportRoutingGuides = (selectedRoutingGuides) => {
  // formula to convert pixels amount into width in Calibri latter measure
  const pixelsToCalibriMeasure = (pixels) => (pixels - 5) / 7;

  const applyHeaderCellsStyle = (sheet) => {
    sheet.getRow(1).eachCell((cell) => {
      cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF0000' },
      };
    });
  };

  const sheetName = 'RoutingGuides ';
  const workbook = new ExcelJs.Workbook();

  const RGSheet = workbook.addWorksheet('RG');
  RGSheet.columns = [
    { header: 'Routing Guide Name', key: 'name', width: pixelsToCalibriMeasure(210) },
    { header: 'Country', key: 'country', width: pixelsToCalibriMeasure(115) },
    { header: 'Transportation Mode', key: 'transportationMode', width: pixelsToCalibriMeasure(175) },
    { header: 'Transportation Group', key: 'transportationGroup', width: pixelsToCalibriMeasure(175) },
  ];

  // Sheet: RG
  RGSheet.addRows(
    selectedRoutingGuides.map((value) => {
      const rg = value.routingHeader;

      return {
        name: rg.name,
        country: rg.country || '-',
        transportationMode: rg.transportationMode || '-',
        transportationGroup: rg.transportationGroup || '-',
      };
    }),
  );
  applyHeaderCellsStyle(RGSheet);

  // Sheet: Approvers
  const approversSheet = workbook.addWorksheet('Approvers');
  approversSheet.columns = [
    { header: 'Routing Guide Name', key: 'rgName', width: pixelsToCalibriMeasure(210) },
    { header: 'Collaboratots', key: 'name', width: pixelsToCalibriMeasure(190) },
    { header: 'Email', key: 'email', width: pixelsToCalibriMeasure(290) },
    { header: 'Type of Access', key: 'role', width: pixelsToCalibriMeasure(150) },
  ];

  approversSheet.addRows(
    selectedRoutingGuides.reduce((acc, value) => {
      const rg = value.routingHeader;

      const collaborators = value.collaborators.map((value) => ({
        rgName: rg.name,
        name: value.name || '-',
        email: value.email || '-',
        role: value.role || '-',
      }));
      acc.push(...collaborators);

      return acc;
    }, []),
  );
  applyHeaderCellsStyle(approversSheet);

  // Sheet: Providers
  const providersSheet = workbook.addWorksheet('Providers');
  providersSheet.columns = [
    { header: 'Routing Guide Name', key: 'rgName', width: pixelsToCalibriMeasure(210) },
    { header: 'SAP Vendor ID', key: 'sapVendorId', width: pixelsToCalibriMeasure(110) },
    { header: 'Transportation Providers', key: 'transportationProvider', width: pixelsToCalibriMeasure(300) },
    { header: 'Provider SCAC', key: 'scac', width: pixelsToCalibriMeasure(110) },
    { header: 'Provider E-mail', key: 'email', width: pixelsToCalibriMeasure(200) },
  ];

  providersSheet.addRows(
    selectedRoutingGuides.reduce((acc, value) => {
      const rg = value.routingHeader;

      const carriers = value.carriers.map((value) => ({
        rgName: rg.name,
        // TODO: change if field will in request
        sapVendorId: '-',
        transportationProvider: value.transportationProvider || '-',
        scac: value.scac || '-',
        email: value.email || '-',
      }));
      acc.push(...carriers);

      return acc;
    }, []),
  );
  applyHeaderCellsStyle(providersSheet);

  workbook.xlsx.writeBuffer().then((buffer) => {
    writeFileAndDownload(`${sheetName} ${new Date().toLocaleDateString()}`, buffer);
  });
};
